import React, { useEffect, useCallback } from 'react';

export interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          aria-label="Close privacy policy"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="overflow-y-auto max-h-[90vh] p-8">
          <h1 id="privacy-modal-title" className="text-3xl font-bold text-white mb-6">
            Privacy Policy
          </h1>

          <div className="prose prose-invert prose-slate max-w-none">
            <section className="mb-8">
              <p className="text-slate-300 mb-4">
                <strong className="text-white">Effective Date:</strong> January 16, 2026
              </p>
              <p className="text-slate-300 mb-4">
                TradeRewind ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our trading analytics platform and services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">1.1 Personal Information</h3>
              <p className="text-slate-300 mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Name and email address</li>
                <li>Account credentials (username and password)</li>
                <li>Payment and billing information</li>
                <li>Profile information and preferences</li>
                <li>Communications with us (support tickets, emails)</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">1.2 Trading and Usage Data</h3>
              <p className="text-slate-300 mb-4">
                When you use our services, we automatically collect:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Trading symbols and watchlists you view</li>
                <li>Chart configurations and technical indicators used</li>
                <li>Analysis requests and AI interactions</li>
                <li>Virtual trading activity and paper trading data</li>
                <li>Replay session data and timestamps</li>
                <li>Session history and saved analyses</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">1.3 Technical Information</h3>
              <p className="text-slate-300 mb-4">
                We collect technical data about your device and usage:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>IP address and geolocation data</li>
                <li>Browser type and version</li>
                <li>Device information (type, operating system)</li>
                <li>Log data (access times, pages viewed, errors)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-300 mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li><strong className="text-white">Service Delivery:</strong> To provide, maintain, and improve our trading analytics platform</li>
                <li><strong className="text-white">Personalization:</strong> To customize your experience and provide relevant market insights</li>
                <li><strong className="text-white">AI Analysis:</strong> To generate trading signals and market analysis using our AI models</li>
                <li><strong className="text-white">Communication:</strong> To send you service updates, security alerts, and support messages</li>
                <li><strong className="text-white">Analytics:</strong> To understand usage patterns and improve our services</li>
                <li><strong className="text-white">Security:</strong> To detect, prevent, and address fraud and security issues</li>
                <li><strong className="text-white">Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">3. Data Sharing and Disclosure</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Third-Party Service Providers</h3>
              <p className="text-slate-300 mb-4">
                We share information with trusted third-party service providers who assist us in operating our platform:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li><strong className="text-white">Authentication Services:</strong> Clerk for user authentication and account management</li>
                <li><strong className="text-white">Market Data Providers:</strong> For real-time and historical market data</li>
                <li><strong className="text-white">Cloud Infrastructure:</strong> For hosting and data storage</li>
                <li><strong className="text-white">Payment Processors:</strong> For subscription billing and payments</li>
                <li><strong className="text-white">Analytics Services:</strong> For usage analytics and performance monitoring</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">3.2 Legal Requirements</h3>
              <p className="text-slate-300 mb-4">
                We may disclose your information if required by law or in response to valid requests by public authorities, including to meet national security or law enforcement requirements.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">3.3 Business Transfers</h3>
              <p className="text-slate-300 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">3.4 What We Don't Share</h3>
              <p className="text-slate-300 mb-4">
                We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">4. Data Security</h2>
              <p className="text-slate-300 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>Secure authentication with multi-factor authentication options</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and employee training on data protection</li>
                <li>Incident response procedures for data breaches</li>
              </ul>
              <p className="text-slate-300 mb-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">5. Cookies and Tracking Technologies</h2>
              <p className="text-slate-300 mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li><strong className="text-white">Essential Cookies:</strong> Required for authentication and core functionality</li>
                <li><strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how you use our services</li>
                <li><strong className="text-white">Session Storage:</strong> Store temporary data for your trading session</li>
              </ul>
              <p className="text-slate-300 mb-4">
                You can control cookies through your browser settings, but disabling certain cookies may limit functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention</h2>
              <p className="text-slate-300 mb-4">
                We retain your information for as long as necessary to provide our services and comply with legal obligations:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li><strong className="text-white">Account Data:</strong> Retained while your account is active and for a reasonable period after closure</li>
                <li><strong className="text-white">Trading History:</strong> Retained for the duration specified in your subscription plan</li>
                <li><strong className="text-white">Payment Records:</strong> Retained as required by tax and accounting regulations</li>
                <li><strong className="text-white">Log Data:</strong> Typically retained for 90 days for security and troubleshooting</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights and Choices</h2>
              <p className="text-slate-300 mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your account and data</li>
                <li><strong className="text-white">Export:</strong> Download your trading data and session history</li>
                <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong className="text-white">Object:</strong> Object to certain data processing activities</li>
              </ul>
              <p className="text-slate-300 mb-4">
                To exercise these rights, please contact us at privacy@tradevision.ai or through your account settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">8. Children's Privacy</h2>
              <p className="text-slate-300 mb-4">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">9. International Data Transfers</h2>
              <p className="text-slate-300 mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">10. California Privacy Rights</h2>
              <p className="text-slate-300 mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to deletion of personal information</li>
                <li>Right to non-discrimination for exercising CCPA rights</li>
              </ul>
              <p className="text-slate-300 mb-4">
                We do not sell personal information as defined by the CCPA.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">11. European Privacy Rights (GDPR)</h2>
              <p className="text-slate-300 mb-4">
                If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR), including:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">12. Third-Party Links</h2>
              <p className="text-slate-300 mb-4">
                Our services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">13. Changes to This Privacy Policy</h2>
              <p className="text-slate-300 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Posting the updated policy on our website</li>
                <li>Sending an email notification to your registered email address</li>
                <li>Displaying a prominent notice in the application</li>
              </ul>
              <p className="text-slate-300 mb-4">
                Your continued use of our services after changes become effective constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">14. Contact Us</h2>
              <p className="text-slate-300 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-800 p-4 rounded-lg mb-4">
                <p className="text-slate-300 mb-2">
                  <strong className="text-white">TradeRewind</strong>
                </p>
                <p className="text-slate-300 mb-1">
                  Email: privacy@tradevision.ai
                </p>
                <p className="text-slate-300 mb-1">
                  Support: support@tradevision.ai
                </p>
                <p className="text-slate-300">
                  Website: www.tradevision.ai
                </p>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-slate-700">
              <p className="text-slate-400 text-sm">
                Last updated: January 16, 2026
              </p>
              <p className="text-slate-400 text-sm mt-2">
                This Privacy Policy is effective as of the date stated above and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
